'use client';

/**
 * INVESTORS ONE-FOCUS DECK (VCTronics raise) — investors are the cards
 * (/api/vctronics/investors, 738 real rows). Focus: WHY = fit rationale +
 * portfolio relevance, PREVIEW = full profile fields, DECIDE = outreach state
 * + admin deep-link (outreach writes live in the VCTronics admin — parity).
 */
import { useCallback, useEffect, useState } from 'react';
import { OneFocusDeck, stColor, stLabel, CY, type OFItem, type OFAdapter } from './OneFocus';
import { mono, display } from '@/future/primitives';

function Why({ item }: { item: OFItem }) {
  const v = item.raw;
  return (
    <>
      {[['TYPE', v.type], ['FIT', v.fit], ['CONTACT', v.contact], ['EMAIL', v.email || '—'], ['STATUS', stLabel(v.outreach_status)]].map(([k, val]) => (
        <div key={k as string} className="flex justify-between" style={{ padding: '4px 0', gap: 10 }}>
          <span style={{ ...mono(15, '.16em'), color: '#7fa9bd', flexShrink: 0 }}>{k}</span>
          <span style={{ ...mono(16, '.04em'), color: (k === 'FIT' && val === 'Non-Fitting') ? '#ff7d8a' : '#d7f2ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{val as string}</span>
        </div>
      ))}
      {v.relevant_portfolio && (
        <div style={{ marginTop: 14, borderTop: '1px solid rgba(125,232,255,.12)', paddingTop: 10 }}>
          <div style={{ ...mono(14, '.26em'), color: '#49677a', marginBottom: 6 }}>PORTFOLIO RELEVANCE</div>
          <div style={{ ...display(23, 300), color: 'var(--uif-t-body)', lineHeight: 1.65 }}>{v.relevant_portfolio}</div>
        </div>
      )}
    </>
  );
}

function Preview({ item }: { item: OFItem }) {
  const v = item.raw;
  const fields = Object.entries(v).filter(([k, val]) => typeof val === 'string' && val && !['id', 'slug', 'name'].includes(k));
  return (
    <>
      <div style={{ ...mono(14, '.26em'), color: '#49677a', marginBottom: 10 }}>FULL PROFILE · LIVE ROW</div>
      {fields.map(([k, val]) => (
        <div key={k} style={{ marginBottom: 10 }}>
          <div style={{ ...mono(13, '.2em'), color: '#49677a', marginBottom: 2 }}>{k.replace(/_/g, ' ').toUpperCase()}</div>
          <div style={{ ...display(23, 300), color: '#c4d8e4', lineHeight: 1.55 }}>{String(val)}</div>
        </div>
      ))}
    </>
  );
}

function Decide({ item }: { item: OFItem }) {
  const v = item.raw;
  const FLOW = ['not_contacted', 'emailed', 'viewed', 'replied', 'meeting', 'passed'];
  const cur = FLOW.indexOf(v.outreach_status || 'not_contacted');
  return (
    <>
      <div style={{ ...mono(14, '.24em'), color: '#49677a', marginBottom: 8 }}>OUTREACH PIPELINE</div>
      <div className="flex flex-col" style={{ gap: 6, marginBottom: 16 }}>
        {FLOW.map((s, i) => (
          <div key={s} className="flex items-center" style={{ gap: 10, opacity: i === cur ? 1 : .45 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: i === cur ? stColor(s) : 'rgba(125,232,255,.15)', boxShadow: i === cur ? `0 0 10px ${stColor(s)}` : 'none', flexShrink: 0 }} />
            <span style={{ ...mono(16, '.2em'), color: i === cur ? stColor(s) : '#56788c' }}>{stLabel(s)}</span>
          </div>
        ))}
      </div>
      {v.email && (
        <a href={`mailto:${v.email}`} style={{ textDecoration: 'none' }}>
          <div style={{ width: '100%', padding: '14px 0', borderRadius: 12, color: '#04121a', background: 'linear-gradient(180deg,#a8eeff,#5fd4ef)', boxShadow: '0 0 24px rgba(0,240,255,.4)', ...mono(18, '.28em'), textAlign: 'center', cursor: 'pointer' }}>✉ EMAIL {String(v.contact || '').split(' ')[0]?.toUpperCase() || 'CONTACT'}</div>
        </a>
      )}
      <a href="https://vctronics.com/admin" target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
        <div style={{ width: '100%', padding: '10px 0', marginTop: 10, borderRadius: 12, background: 'transparent', border: '1px solid rgba(125,232,255,.3)', color: CY, ...mono(16, '.26em'), textAlign: 'center', cursor: 'pointer' }}>ADMIN ↗</div>
      </a>
      <div style={{ ...mono(13, '.12em'), color: '#3d4654', marginTop: 10, textAlign: 'center' }}>OUTREACH WRITES LIVE IN THE ADMIN</div>
    </>
  );
}

export function InvDeckSurface() {
  const [items, setItems] = useState<OFItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const load = useCallback(() => {
    fetch('/api/vctronics/investors').then(r => r.json()).then(d => {
      const rows: any[] = Array.isArray(d) ? d : d.investors || [];
      const rank = (v: any) => (v.fit === 'Fitting' ? 0 : v.fit === 'Maybe' ? 1 : 2);
      rows.sort((a, b) => rank(a) - rank(b));
      setItems(rows.map((v: any): OFItem => ({
        id: v.id || v.slug, title: v.name, type: (v.type || 'INVESTOR').toUpperCase(),
        status: v.outreach_status || 'not_contacted',
        kpis: [
          { l: 'FIT', v: (v.fit || '—').toUpperCase().slice(0, 11) },
          { l: 'CONTACT', v: v.contact ? '✓' : '—' },
          { l: 'EMAIL', v: v.email ? '✓' : '—' },
        ],
        taste: v.relevant_portfolio ? String(v.relevant_portfolio).slice(0, 180) : undefined,
        foot: v.email || v.slug, raw: v,
      })));
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const adapter: OFAdapter = {
    kind: 'inv',
    strap: (its) => `ONE FOCUS · ${its.length} INVESTORS · RAISE PIPELINE`,
    decidable: (it) => it.status === 'not_contacted' && !!it.raw.email && it.raw.fit === 'Fitting',
    Why, Preview, Decide,
  };
  return <OneFocusDeck items={items} loaded={loaded} adapter={adapter} refresh={load} />;
}
